import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';

const DEFAULT_SESSION_PARAM = 'session';
const DEFAULT_LANGUAGE_PARAM = 'language';

type DeepLinkExtraParams = Record<string, string | number | boolean | null | undefined>;

type UseSessionDeepLinkParams = {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  sessionValue: string;
  selectedLanguage?: string;
  setSelectedLanguage?: (language: string) => void;
  sessionStarted: boolean;
  isStartingSession?: boolean;
  startSession: () => void | Promise<void>;
  requireLanguage?: boolean;
  canResume?: boolean;
  params?: DeepLinkExtraParams;
  sessionParam?: string;
  languageParam?: string;
};

function applyExtraParams(searchParams: URLSearchParams, params: DeepLinkExtraParams) {
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      searchParams.delete(key);
      return;
    }

    searchParams.set(key, String(value));
  });
}

export function useSessionDeepLink({
  searchParams,
  setSearchParams,
  sessionValue,
  selectedLanguage = '',
  setSelectedLanguage,
  sessionStarted,
  isStartingSession = false,
  startSession,
  requireLanguage = Boolean(setSelectedLanguage),
  canResume = true,
  params = {},
  sessionParam = DEFAULT_SESSION_PARAM,
  languageParam = DEFAULT_LANGUAGE_PARAM,
}: UseSessionDeepLinkParams) {
  const linkedLanguage = searchParams.get(languageParam) || '';
  const sessionResumeActive = searchParams.get(sessionParam) === sessionValue;
  const paramsSignature = JSON.stringify(params);
  const startSessionRef = useRef(startSession);
  const autoStartedKeyRef = useRef<string | null>(null);
  const manualStartInFlightRef = useRef(false);

  useEffect(() => {
    startSessionRef.current = startSession;
  }, [startSession]);

  useEffect(() => {
    if (!linkedLanguage || selectedLanguage || !setSelectedLanguage) return;
    setSelectedLanguage(linkedLanguage);
  }, [linkedLanguage, selectedLanguage, setSelectedLanguage]);

  const sessionReturnSearch = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    next.set(sessionParam, sessionValue);
    if (selectedLanguage) {
      next.set(languageParam, selectedLanguage);
    }
    applyExtraParams(next, params);
    return `?${next.toString()}`;
  }, [languageParam, paramsSignature, searchParams, selectedLanguage, sessionParam, sessionValue]);

  const markSessionInUrl = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set(sessionParam, sessionValue);
    if (selectedLanguage) {
      next.set(languageParam, selectedLanguage);
    }
    applyExtraParams(next, params);
    setSearchParams(next, { replace: true });
  }, [languageParam, paramsSignature, searchParams, selectedLanguage, sessionParam, sessionValue, setSearchParams]);

  const startLinkedSession = useCallback(async () => {
    if (manualStartInFlightRef.current || sessionStarted || isStartingSession) return;

    manualStartInFlightRef.current = true;
    try {
      markSessionInUrl();
      await startSessionRef.current();
    } finally {
      manualStartInFlightRef.current = false;
    }
  }, [isStartingSession, markSessionInUrl, sessionStarted]);

  useEffect(() => {
    if (!sessionResumeActive) {
      autoStartedKeyRef.current = null;
      return;
    }

    const hasRequiredLanguage = !requireLanguage || Boolean(selectedLanguage);
    if (!hasRequiredLanguage || !canResume || sessionStarted || isStartingSession) return;

    const autoStartKey = `${sessionValue}:${selectedLanguage}:${paramsSignature}`;
    if (autoStartedKeyRef.current === autoStartKey) return;

    autoStartedKeyRef.current = autoStartKey;
    void startSessionRef.current();
  }, [canResume, isStartingSession, paramsSignature, requireLanguage, selectedLanguage, sessionResumeActive, sessionStarted, sessionValue]);

  return {
    sessionResumeActive,
    sessionReturnSearch,
    startLinkedSession,
    markSessionInUrl,
  };
}
